/**
 * Rubrique Matériel, côté stock (refonte v4 du 17/10/2026) : l'accueil, ce
 * qu'il y a où, les articles, les véhicules et dépôts, ce qui est à surveiller,
 * puis les fiches et saisies (adresses historiques gardées : avis, e-mails).
 * Un emplacement ou un véhicule se crée et se modifie avec `stock.manage`,
 * comme l'API l'exige, un inventaire aussi (décision 15) ; l'ancienne adresse
 * `/stock` mène à la bonne liste.
 */
import { lazy } from 'react';
import type { AppRoute } from '../../app/route-types.js';
import { MATERIEL_PATHS } from '../../app/materiel-sections.js';
import { LegacyStockRedirect } from './MaterielRedirects.js';

const MaterielPage = lazy(() => import('./MaterielPage.js').then((m) => ({ default: m.MaterielPage })));
const StockLevelsPage = lazy(() => import('./StockLevelsPage.js').then((m) => ({ default: m.StockLevelsPage })));
const ArticlesPage = lazy(() => import('./ArticlesPage.js').then((m) => ({ default: m.ArticlesPage })));
const LocationsPage = lazy(() => import('./LocationsPage.js').then((m) => ({ default: m.LocationsPage })));
const WatchPage = lazy(() => import('./WatchPage.js').then((m) => ({ default: m.WatchPage })));
const StockMovementPage = lazy(() => import('./StockMovementPage.js').then((m) => ({ default: m.StockMovementPage })));
const VehiclePage = lazy(() => import('./VehiclePage.js').then((m) => ({ default: m.VehiclePage })));
const ArticleNewPage = lazy(() => import('./ArticlePages.js').then((m) => ({ default: m.ArticleNewPage })));
const ArticleEditPage = lazy(() => import('./ArticlePages.js').then((m) => ({ default: m.ArticleEditPage })));
const InventoriesPage = lazy(() => import('./InventoriesPage.js').then((m) => ({ default: m.InventoriesPage })));
const InventoryNewPage = lazy(() => import('./InventoryNewPage.js').then((m) => ({ default: m.InventoryNewPage })));
const InventoryPage = lazy(() => import('./InventoryPage.js').then((m) => ({ default: m.InventoryPage })));
const InventoryCountPage = lazy(() => import('./InventoryCountPage.js').then((m) => ({ default: m.InventoryCountPage })));
const InventoryValidatePage = lazy(() => import('./InventoryValidatePage.js').then((m) => ({ default: m.InventoryValidatePage })));
const ArticleDetailPage = lazy(() => import('./ArticleDetailPage.js').then((m) => ({ default: m.ArticleDetailPage })));

const manage = { anyOf: ['stock.manage'] } as const;
const inventories = { anyOf: ['stock.manage'], fallback: MATERIEL_PATHS.accueil } as const;

export const stockRoutes: AppRoute[] = [
  { path: MATERIEL_PATHS.accueil, element: <MaterielPage /> },
  { path: MATERIEL_PATHS.stock, element: <StockLevelsPage /> },
  { path: MATERIEL_PATHS.articles, element: <ArticlesPage /> },
  { path: MATERIEL_PATHS.emplacements, element: <LocationsPage /> },
  { path: MATERIEL_PATHS.surveiller, element: <WatchPage /> },
  { path: MATERIEL_PATHS.inventaires, element: <InventoriesPage />, gate: inventories },
  { path: '/stock', element: <LegacyStockRedirect /> },
  { path: '/stock/mouvement', element: <StockMovementPage />, gate: { anyOf: ['stock.consume_transfer'] } },
  { path: '/stock/articles/nouveau', element: <ArticleNewPage />, gate: { anyOf: ['stock.manage'] } },
  { path: '/stock/articles/:id', element: <ArticleDetailPage /> },
  { path: '/stock/articles/:id/modifier', element: <ArticleEditPage />, gate: { anyOf: ['stock.manage'] } },
  { path: '/stock/emplacements/nouveau', element: <VehiclePage />, gate: manage },
  { path: '/stock/emplacements/:id', element: <VehiclePage />, gate: manage },
  { path: '/stock/vehicules/nouveau', element: <VehiclePage kind="vehicule" />, gate: manage },
  { path: '/stock/vehicules/:id', element: <VehiclePage />, gate: manage },
  // Inventaires (décision 15 du 19/10/2026) : lancer, compter, valider ; tout sous `stock.manage`.
  { path: '/stock/inventaires/nouveau', element: <InventoryNewPage />, gate: inventories },
  { path: '/stock/inventaires/:id', element: <InventoryPage />, gate: inventories },
  { path: '/stock/inventaires/:id/comptage', element: <InventoryCountPage />, gate: inventories },
  { path: '/stock/inventaires/:id/validation', element: <InventoryValidatePage />, gate: inventories },
];
